import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import PillButton from '../buttons/PillButton';
import Background from '../partials/Background';
import Image from '../partials/Image';
import PlaceholderText from '../partials/PlaceholderText';
import Sponsor from '../partials/Sponsor';
import Surtitle from '../partials/Surtitle';
import CollectionHeaderDescription from '../typography/CollectionHeaderDescription';
import HeaderDescription from '../typography/HeaderDescription';
import HeaderTitle from '../typography/HeaderTitle';
import IndexDefaultHeader from './IndexDefaultHeader';

import styles from '../../styles/headers/collection-default-header.module.css';

export interface CollectionDefaultHeaderProps {
    type?: string | null;
    brands?: Brand[] | null;
    sponsors?: Sponsor[] | null;
    title?: string | null;
    surtitle?: string | null;
    subtitle?: string | null;
    description?: string | null;
    background?: Background | null;
    callToAction?: CallToAction | null;
    color?: string;
    image?: Image | null;
    placeholder?: boolean;
    className?: string | null;
}

function CollectionDefaultHeader({
    brands = null,
    title = null,
    surtitle = null,
    subtitle = null,
    description = null,
    image = null,
    callToAction = null,
    sponsors = null,
    placeholder = false,
    background = null,
    className = null,
}: CollectionDefaultHeaderProps) {
    const [sponsor = null] = sponsors || [];
    const [brand = null] = brands || [];
    const { handle: brandHandle } = brand || {};

    const { label: ctaLabel = null, url: ctaUrl = null } = callToAction || {};

    const hasOnlyTitle = description === null && background === null && image === null;
    if (hasOnlyTitle && !placeholder) {
        return <IndexDefaultHeader brand={brand} title={title} className={className} />;
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles[brandHandle]]: brandHandle !== null,
                    [styles.imageOnly]: background === null && image !== null,
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.inner}>
                <div className={styles.imagesContainer}>
                    {background !== null ? (
                        <div className={styles.backgroundContainer}>
                            <Background
                                {...background}
                                className={styles.background}
                                imageClassName={styles.image}
                                pictureClassName={styles.picture}
                            />
                        </div>
                    ) : null}

                    {background === null && image !== null ? (
                        <div className={styles.backgroundContainer}>
                            <Image
                                className={styles.image}
                                media={image}
                                size="responsive"
                                pictureClassName={styles.picture}
                            />
                        </div>
                    ) : null}

                    <div className={styles.imageContainer}>
                        {image !== null && background !== null ? (
                            <Image
                                className={styles.image}
                                media={image}
                                size="responsive"
                                pictureClassName={styles.picture}
                            />
                        ) : null}
                    </div>
                </div>

                <div className={styles.boxContainer}>
                    <div className={styles.box}>
                        {surtitle !== null ? (
                            <Surtitle surtitle={surtitle} className={styles.surtitle} />
                        ) : null}
                        <HeaderTitle brand={brandHandle} className={styles.title}>
                            {placeholder ? <PlaceholderText lines={2} /> : title}
                        </HeaderTitle>
                        {subtitle !== null ? (
                            <HeaderDescription className={styles.subtitle}>
                                {subtitle}
                            </HeaderDescription>
                        ) : null}
                        {sponsor !== null ? (
                            <Sponsor
                                {...sponsor}
                                withImage
                                className={styles.sponsor}
                                imageClassName={styles.sponsorImage}
                            />
                        ) : null}
                        {placeholder ? (
                            <CollectionHeaderDescription className={styles.description}>
                                <PlaceholderText lines={4} />
                            </CollectionHeaderDescription>
                        ) : null}
                        {description !== null && !placeholder ? (
                            <CollectionHeaderDescription
                                className={styles.description}
                                html={description}
                            />
                        ) : null}
                        {callToAction !== null ? (
                            <PillButton href={ctaUrl} external className={styles.button}>
                                {ctaLabel !== null ? (
                                    <span dangerouslySetInnerHTML={{ __html: ctaLabel }} />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="En savoir plus..."
                                        description="Button label"
                                    />
                                )}
                            </PillButton>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CollectionDefaultHeader;
